/**
 * Pricing helpers — single source of truth for the workshop's quote /
 * invoice line-item math. Imported by ServiceNotesModal, InvoiceEdit,
 * CustomItemSearch and any future surface that prices a row.
 *
 * Why a shared helper:
 *   The line-total math must be IDENTICAL everywhere it's built or shown —
 *   Quote Builder, Booking quote builder, Service Notes, Invoice Edit,
 *   Templates, the customer PDF and the SMS. This module is the single
 *   source of truth so the formula only lives in ONE place.
 *
 * Formula (SM decision Aug-2026 — LABOUR IS FLAT, parts scale with qty):
 *
 *     parts_per_unit  = RRP × (1 + margin% / 100)
 *     labour_charge   = hrs × rate            (ONE-TIME, whole line)
 *     part_and_labour → line_total = (parts_per_unit × qty) + labour_charge
 *     part            → line_total = parts_per_unit × qty
 *     labour          → line_total = labour_charge          (qty ignored)
 *     tyre            → line_total = ((ORP × (1 + margin%/100)) + flat_rate) × qty
 *                       margin is MANUAL (SM types it; blank = 0%) — no RRP
 *                       concept on tyre rows. Legacy rows saved without a
 *                       margin keep the historical 30% so stored totals
 *                       don't shift.
 *
 *   Labour is charged ONCE for the whole job — it does NOT multiply by qty.
 *   For part_and_labour, `unit_price` is the BLENDED per-unit price
 *   (line_total ÷ qty) so unit_price × qty == line_total (the flat labour is
 *   spread across qty). part → parts_charge; labour-only → flat labour_charge.
 *   This keeps the Quote Builder, Service Notes, Invoice page, customer PDF
 *   and SMS all consistent for any quantity.
 *
 * Category-aware: 'part' rows zero labour fields; 'labour' rows zero
 * parts fields; 'part_and_labour' (default) honours both.
 *
 * ORP → RRP markup (when SM enters ORP $10, workshop ORP margin 30%):
 *   RRP_auto = ORP × (1 + orp_margin% / 100)  → $13.00
 * The ORP margin % is configurable per workshop in Pricing Defaults
 * (backend `default_orp_margin_pct`, falls back to 30). SM can override
 * RRP afterwards. Auto-fill only fires while TYPING an ORP — existing
 * saved rows are never recalculated.
 */

export const DEFAULT_ORP_MARGIN_PCT = 30;
export const DEFAULT_MARGIN_PCT = 40;
export const TYRE_MARGIN_PCT = 30;
export const ROW_CATEGORIES = ['part_and_labour', 'part', 'labour', 'tyre'];

/** RRP = ORP × (1 + marginPct/100), rounded to cents. marginPct comes from
 *  the workshop Pricing Defaults (falls back to 30 when not provided).
 *  Returns '' for non-positive ORP so the SM can clear the input to blank. */
export const orpToRrp = (orp, marginPct) => {
  const n = Number(orp);
  if (!isFinite(n) || n <= 0) return '';
  const m = (marginPct == null || marginPct === '') ? DEFAULT_ORP_MARGIN_PCT : (Number(marginPct) || 0);
  return Math.round(n * (1 + m / 100) * 100) / 100;
};

/**
 * Compute the structured pricing breakdown for a row.
 *
 * @param {Object} row    line item / notes row with structured fields
 * @param {Number} workshopRate  fallback labour rate when the row has none
 * @returns {{parts_charge, parts_subtotal, labour_charge, line_total, unit_price_avg, cost_margin_pct}}
 */
export const computeRowPricing = (row, workshopRate = 0) => {
  const cat = row.category || 'part_and_labour';
  const qty = Number(row.quantity) || 1;
  const partsCost = Number(row.parts_cost_price) || 0;

    // TYRE rows have their own formula: (ORP × (1 + margin%)) + flat_rate
    // per tyre. Margin is MANUAL (SM types it; blank/'' = 0%) — there is
    // no RRP concept on tyre rows. flat_rate covers fitting/balancing/
    // valve/disposal which scales WITH each tyre (not flat per-line).
    //
    // Legacy compatibility: rows saved under the old locked-30% rule carry
    // NO parts_margin_pct (null/undefined) → treated as 30 so stored
    // quotes / templates keep rendering their original totals.
    if (cat === 'tyre') {
      const orp = (Number(row.parts_cost_price) > 0)
        ? Number(row.parts_cost_price)
        : Number(row.parts_price) || 0;
      const flatRate = Number(row.tyre_flat_rate) || 0;
      const mRaw = row.parts_margin_pct;
      const margin = (mRaw == null) ? TYRE_MARGIN_PCT : (Number(mRaw) || 0);
      const parts_charge = round2(orp * (1 + margin / 100));
      const per_tyre = round2(parts_charge + flatRate);
      const line_total = round2(per_tyre * qty);
      const cost_margin_pct = orp > 0 && Number(row.parts_price) > 0
        ? ((Number(row.parts_price) - orp) / orp) * 100
        : null;
      return {
        parts_charge,
        parts_subtotal: round2(parts_charge * qty),
        labour_charge: round2(flatRate * qty),
        line_total,
        unit_price_avg: per_tyre,
        cost_margin_pct,
      };
    }

  const wantsParts = cat === 'part' || cat === 'part_and_labour';
  const wantsLabour = cat === 'labour' || cat === 'part_and_labour';
  const partsPrice = wantsParts ? (Number(row.parts_price) || 0) : 0;
  const margin = wantsParts ? (Number(row.parts_margin_pct) || 0) : 0;
  const hrs = wantsLabour ? (Number(row.labour_hours) || 0) : 0;
  const rate = wantsLabour ? (Number(row.labour_rate) || workshopRate || 0) : 0;

  const parts_charge = round2(partsPrice * (1 + margin / 100));     // per-unit
  const parts_subtotal = round2(parts_charge * qty);                 // parts scale with qty
  const labour_charge = round2(hrs * rate);                          // FLAT — whole line

  // NEW RULE (SM decision Aug-2026): LABOUR is a ONE-TIME charge for the
  // whole line; PARTS scale with qty.
  //   part_and_labour → line_total = (parts_charge × qty) + labour_charge (flat)
  //   part            → line_total = parts_charge × qty
  //   labour          → line_total = labour_charge          (flat; qty ignored)
  // unit_price_avg for part_and_labour is the BLENDED per-unit price
  // (line_total ÷ qty) so unit_price × qty == line_total (the flat labour is
  // spread across qty) — this is what the "Unit Price" column shows. part =
  // parts_charge; labour-only = flat labour_charge.
  let line_total, unit_price_avg;
  if (cat === 'part') {
    line_total = parts_subtotal;
    unit_price_avg = parts_charge;
  } else if (cat === 'labour') {
    line_total = labour_charge;
    unit_price_avg = labour_charge;
  } else { // part_and_labour (default)
    line_total = round2(parts_subtotal + labour_charge);
    unit_price_avg = qty ? round2(line_total / qty) : round2(parts_charge + labour_charge);
  }
  // Effective margin vs supplier cost — informational only.
  const cost_margin_pct = partsCost > 0
    ? ((partsPrice - partsCost) / partsCost) * 100
    : null;

  return { parts_charge, parts_subtotal, labour_charge, line_total, unit_price_avg, cost_margin_pct };
};

/** Compact alias — most callers only care about the $ total. */
export const computeLineTotal = (row, workshopRate = 0) =>
  computeRowPricing(row, workshopRate).line_total;

/**
 * Template-aware line total. Falls back to `qty × unit_price` when the
 * line was saved in the legacy shape (only `unit_price` set, structured
 * fields missing). Used by BOTH the Manage Templates UI and the
 * "Add Custom Item" picker so they display identical totals — eliminates
 * the bug where Manage showed $295 but picker showed $0 for the same row.
 */
export const computeTemplateLineTotal = (row, workshopRate = 0) => {
  const structured = computeLineTotal(row, workshopRate);
  if (structured > 0) return structured;
  return (Number(row.quantity) || 1) * (Number(row.unit_price) || 0);
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
